import { ICategory, Option } from '../category/category.types';
import { MediaType } from '../featuredAd/featuredAd.types';
import { Meta } from '../files/files.types';
import { RecordStatus } from '../menu/enums.types';
import { IItem, Tax } from '../menu/item.types';
import {
  FileForType,
  FileStoreAspectRatio,
  FileStoreStatus,
  IModifier,
  ModifierGroup,
  NestedModifiers,
  Tag,
} from '../menu/menu.types';
import { StationItemsMappingType } from '../restaurant/stations.types';
import { FormattedWeekDay } from '../setting/business-hours.types';

export enum MenuLayout {
  TwoCrossTwo = '2x2',
  ThreeCrossTwo = '3x2',
}

export type NuggetConfigurationRequest = {
  id?: number;
  menuLayout: MenuLayout;
  isVoiceEnabled: boolean;
  isAdsOnlyJourney: boolean;
  restaurantId: number;
};

export type NuggetConfigurationResponse = {
  id: number;
  settingId: number;
  menuLayout: MenuLayout;
  isVoiceEnabled: boolean;
  isAdsOnlyJourney: boolean;
  restaurantId: number;
  featureFlags: Record<NuggetFeature, IFeatureFlagWithLabel>;
  status: RecordStatus;
};

export type EditNuggetConfigurationRequest = {
  id: number;
  restaurantId: number;
  menuLayout: MenuLayout;
  isVoiceEnabled: boolean;
  isAdsOnlyJourney: boolean;
};

export type NuggetConfigurationCategoryRequest = {
  categoryId: number;
  sortOrder: number;
};

export type NuggetConfigurationCategoryResponse = {
  id: number;
  nuggetConfigurationId: number;
  categoryId: number;
  sortOrder: number;
  categoryName: string;
  status: RecordStatus;
  category?: ICategory;
};

export type NuggetConfigurationItemRequest = {
  itemId: number;
  categoryId: number;
  sortOrder: number;
};

export type NuggetConfigurationItemResponse = {
  id: number;
  nuggetConfigurationId: number;
  categoryId: number;
  itemId: number;
  sortOrder: number;
  status: RecordStatus;
  item?: IItem;
};

export type NuggetMenuResponse = {
  nuggetConfiguration: NuggetConfigurationResponse;
  nuggetConfigurationCategory: NuggetConfigurationCategoryResponse[];
  nuggetConfigurationItem: NuggetConfigurationItemResponse[];
};

export interface INuggetConfiguration {
  menuLayout: MenuLayout;
  isVoiceEnabled: boolean;
  isAdsOnlyJourney: boolean;
  restaurantId: number;
}

export interface IEditNuggetConfiguration {
  id: number;
  data: {
    menuLayout?: MenuLayout;
    featureFlags?: NuggetConfigurationExtendedFeatureFlags;
    restaurantId: number;
  };
}

export interface INuggetConfigurationCategory {
  nuggetConfigurationId: number;
  categoryId: number;
  sortOrder: number;
}

export interface INuggetConfigurationItem {
  nuggetConfigurationId: number;
  itemId: number;
  categoryId: number;
  sortOrder: number;
}

export interface ICategoryItem {
  id: number;
  sortOrder: number;
  status: string;
  category: {
    id: number;
    categoryName: string;
  };
  item: {
    id: number;
    itemName: string;
    itemPrice: number;
  };
}

export interface INuggetFormData {
  categories: {
    label: string;
    value: number;
    items: Option[];
  }[];
}

export interface ISelectedItem {
  itemId: number;
  categoryId: number;
  sortOrder: number;
  label?: string;
  img?: string;
}

export interface ICreateNuggetMenuRequest {
  nuggetConfiguration: NuggetConfigurationRequest;
  nuggetConfigurationCategory: NuggetConfigurationCategoryRequest[];
  nuggetConfigurationItem: NuggetConfigurationItemRequest[];
}

export interface INuggetConfigurationDataResponse {
  nuggetConfiguration: NuggetConfigurationResponse;
  nuggetConfigurationCategory: NuggetConfigurationCategoryResponse[];
  nuggetConfigurationItem: NuggetConfigurationItemResponse[];
}

interface IModifierWithNested extends IModifier {
  nestedModifiers?: NestedModifiers[];
}

export interface INuggetFeaturedMenuResponse {
  categoryId: number;
  sortOrder: number;
  categoryName: string;
  color: string;
  image: string;
  tag?: Tag[];
  items: {
    itemId: number;
    sortOrder: number;
    itemName: string;
    itemDescription: string;
    itemPrice: number;
    itemPicture: string;
    calories: number;
    quantity?: number;
    preparationTime?: string;
    stockStatus?: string;
    posDisplayName?: string;
    kdsName?: string;
    modifierGroup?: ModifierGroup[];
    modifiers?: IModifierWithNested[];
    stationItemsMapping?: StationItemsMappingType[];
    tag?: Tag[];
    taxes?: Tax[];
    startTime: string;
    endTime: string;
  }[];
}

export interface IFeaturedAdDataResponse {
  id: number;
  nuggetConfigurationId: number;
  isCampaign: boolean;
  title: string;
  url: string;
  media: string;
  mediaType: MediaType;
  showOnIdle: boolean;
  showOnPostOrder: boolean;
  duration: string;
  campaignId?: number;
  sortOrder: number;
  settingId: number;
  visibilityId: number;
  isDefault: boolean;
  daySchedule: FormattedWeekDay[];
  status: RecordStatus;
}

export interface ICreateFeaturedAdRequest {
  restaurantId?: number;
  isCampaign: boolean;
  campaignId?: number;
  title: string;
  url: string;
  media: string;
  mediaType: MediaType;
  showOnIdle: boolean;
  showOnPostOrder: boolean;
  duration: string;
  availability: FormattedWeekDay[];
  isAvailabilityDefault: boolean;
}

export interface IUpdateFeaturedAdRequest {
  id: number;
  restaurantId: number;
  settingId?: number;
  visibilitySettingId?: number;
  featuredAd: ICreateFeaturedAdRequest;
}

export type FeaturedAdsListResponse = {
  id: number;
  nuggerConfigurationId: number;
  isCampaign: boolean;
  title: string;
  url: string;
  media: string;
  mediaType: MediaType;
  showOnIdle: boolean;
  showOnPostOrder: boolean;
  duration: string;
  campaignId?: number;
  status: RecordStatus;
}[];

export type CampaignDataResponse = {
  campaigns: {
    id: number;
    title: string;
    content: {
      media: {
        id: number;
        name: string;
        fileUrl: string;
        type: string;
        status: FileStoreStatus;
        aspectRatio: FileStoreAspectRatio;
        size: number;
        businessId: number;
        restaurantId: number;
        fileFor: FileForType;
        fileStoreCategoryId: number;
        meta: Meta;
        createdAt: Date;
      };
    }[];
  }[];
  total: number;
};

export interface FeaturedItem {
  itemId: number;
  sortOrder: number;
}

export interface FeaturedCategory {
  categoryId: number;
  sortOrder: number;
  items: FeaturedItem[];
}
export interface NuggetConfigurationExtendedFeatureFlags {
  showAds: boolean;
  showMenu: boolean;
  enableTips: boolean;
  enableCheckIn: boolean;
  enableReviews: boolean;
  enableCrossSelling: boolean;
  enableItemSelection: boolean;
  enableLoyaltySignup: boolean;
}

export const defaultFeatureFlags = {
  showAds: true,
  showMenu: true,
  enableItemSelection: true,
  enableReviews: true,
  enableCheckIn: false,
  enableTips: true,
  enableCrossSelling: false,
  enableLoyaltySignup: false,
};

export const FEATURE_FLAG_LABELS: Record<string, string> = {
  showAds: 'Show Ads',
  showMenu: 'Show Menu',
  enableItemSelection: 'Enable Item Selection',
  enableReviews: 'Enable Reviews',
  enableCheckIn: 'Enable Check-ins/Call server',
  enableTips: 'Enable Tips',
  enableCrossSelling: 'Enable Cross-Selling',
  enableLoyaltySignup: 'Enable Loyalty Signup',
};

export enum NuggetFeature {
  ShowAds = 'showAds',
  ShowMenu = 'showMenu',
  EnableItemSelection = 'enableItemSelection',
  EnableReviews = 'enableReviews',
  EnableCheckin = 'enableCheckIn',
  EnableTips = 'enableTips',
  EnableCrossSelling = 'enableCrossSelling',
  EnableLoyaltySignup = 'enableLoyaltySignup',
}

export interface IFeatureFlagWithLabel {
  label: string;
  value: boolean;
}

export type mutatedNuggetConfig = Omit<NuggetConfigurationResponse, 'featureFlags'> & {
  featureFlags: Record<NuggetFeature, IFeatureFlagWithLabel>;
};

export type EditNuggetFeatureFlagsRequest = {
  nuggetConfigurationId: number;
  restaurantId: number;
  featureFlags: NuggetConfigurationExtendedFeatureFlags;
};

export interface IEditNuggetFeatureFlagsRequest {
  nuggetConfiguration: EditNuggetFeatureFlagsRequest;
}
